import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import { useUnit } from 'effector-react';

import { buildingsApi } from '../api/buildings';
import { heatApi } from '../api/heat';
import { reportsApi } from '../api/reports';
import { $tenantDataStartDate } from '../store/auth';
import { formatDateOnly, formatPeriodRange as formatPeriodRangeSafe, localMonthStartDate } from '../utils/dates';
import { buildFileName, downloadBlobAsFile } from '../utils/download';

const surfaceSx = {
  borderRadius: 4,
  border: '1px solid var(--border-color)',
  background: 'var(--surface-bg)',
  boxShadow: 'none',
};

const inputSx = {
  '& .MuiInputLabel-root': {
    fontSize: 13,
    color: '#64748B',
  },
  '& .MuiOutlinedInput-root': {
    minHeight: 40,
    background: '#F8FAFD',
    borderRadius: 2,
    boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.75)',
    '& fieldset': {
      borderColor: 'rgba(38, 65, 94, 0.16)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(46, 115, 184, 0.48)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#2E73B8',
      borderWidth: 1,
    },
  },
};

const autocompletePaperSx = {
  mt: 0.6,
  borderRadius: 2,
  border: '1px solid rgba(38, 65, 94, 0.16)',
  boxShadow: '0 16px 40px rgba(15, 23, 42, 0.14)',
  overflow: 'hidden',
  '& .MuiAutocomplete-listbox': {
    p: 0.5,
    maxHeight: 320,
  },
  '& .MuiAutocomplete-option': {
    minHeight: 34,
    borderRadius: 1.25,
    fontSize: 13,
    '&[aria-selected="true"]': {
      backgroundColor: 'rgba(46, 115, 184, 0.1)',
    },
    '&.Mui-focused': {
      backgroundColor: 'rgba(46, 115, 184, 0.08)',
    },
  },
};

const countChipSx = {
  height: 22,
  borderRadius: '999px',
  fontSize: 12,
  fontWeight: 700,
  background: '#F4F7FB',
  color: '#26415E',
  border: '1px solid rgba(38, 65, 94, 0.12)',
};

const tableHeadCellSx = {
  py: 0.9,
  px: 1.15,
  position: 'sticky',
  top: 0,
  zIndex: 3,
  whiteSpace: 'nowrap',
  fontSize: 12,
  fontWeight: 800,
  color: '#526579',
  background: 'linear-gradient(180deg, #FFFFFF 0%, #F6F9FD 100%)',
  borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
  boxShadow: '0 8px 18px rgba(15, 23, 42, 0.06)',
};

const tableCellSx = {
  py: 0.75,
  px: 1.15,
  fontSize: 13,
  borderColor: 'rgba(15, 23, 42, 0.06)',
  verticalAlign: 'middle',
};

const tableRowHoverSx = {
  cursor: 'pointer',
  transition: 'background-color 0.16s ease, transform 0.16s ease',
  '&:nth-of-type(even)': {
    backgroundColor: 'rgba(248, 250, 252, 0.55)',
  },
  '&:hover': {
    backgroundColor: 'rgba(52, 104, 255, 0.04)',
  },
  '& td': {
    ...tableCellSx,
    borderColor: 'rgba(15, 23, 42, 0.06)',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
  },
};

function formatDate(value) {
  return formatDateOnly(value);
}

function formatDateTime(value) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  const parts = new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(parsed);
  const part = (type) => parts.find((item) => item.type === type)?.value || '';
  return `${part('day')}.${part('month')}.${part('year')} ${part('hour')}:${part('minute')}`;
}

function formatMoney(value) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function formatNumber(value, digits = 2) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
}

function formatPeriodRange(start, end, options = {}) {
  return formatPeriodRangeSafe(start, end, options).replace(' — ', ' - ');
}

function dateKey(value) {
  const normalized = String(value || '').trim();
  return /^\d{4}-\d{2}-\d{2}/.test(normalized) ? normalized.slice(0, 10) : null;
}

function monthKey(value) {
  const normalized = String(value || '').trim();
  return /^\d{4}-\d{2}/.test(normalized) ? normalized.slice(0, 7) : null;
}

function uniqueApartmentCount(rows) {
  const set = new Set();
  rows.forEach((row) => {
    const key = `${row.corps_name || ''}:${row.apartment_number || ''}`;
    if (row.apartment_number) {
      set.add(key);
    }
  });
  return set.size;
}

function hasPeriodReading(row) {
  return row?.current_value != null || row?.current_ts != null;
}

function deriveStandardBasis(withReadings, withoutReadings, completePairs = withReadings) {
  if (withReadings > 0 && (withoutReadings > 0 || completePairs < withReadings)) {
    return 'Показания и добор пропусков';
  }
  if (withReadings > 0) {
    return 'По показаниям приборов';
  }
  return 'Требует уточнения';
}

function deriveUrtCalculationType(report) {
  const apartments = report?.apartments || [];
  const directCount = apartments.filter((item) => item.uses_direct_devices).length;
  const allocatorCount = apartments.filter((item) => item.has_data && !item.uses_direct_devices).length;

  if (directCount > 0 && allocatorCount > 0) {
    return 'УРТ + УПД';
  }
  if (directCount > 0) {
    return 'УПД';
  }
  return 'УРТ';
}

function createCsvBlob(headers, rows) {
  const escapeCell = (value) => {
    const text = value == null ? '' : String(value);
    return `"${text.replace(/"/g, '""')}"`;
  };

  const lines = [
    headers.map((item) => escapeCell(item)).join(';'),
    ...rows.map((row) => row.map((item) => escapeCell(item)).join(';')),
  ];

  return new Blob([`\uFEFF${lines.join('\n')}`], {
    type: 'text/csv;charset=utf-8;',
  });
}

function summaryChip(label, value) {
  return (
    <Paper key={label} variant="outlined" sx={{ ...surfaceSx, p: 1.15, flex: 1, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
        {value}
      </Typography>
    </Paper>
  );
}

function flattenUrtRows(payload) {
  const apartments = payload?.apartments || [];
  const summary = payload?.summary || null;

  return apartments.flatMap((apartment) => {
    const baseRow = {
      apartment_id: apartment.apartment_id,
      apartment_number: apartment.apartment_number || '',
      account_number: apartment.account_number || '',
      corp_name: apartment.corp_name || '',
      area: apartment.area,
      conditional_consumption: apartment.conditional_consumption,
      base_payment: apartment.base_payment,
      constant_payment: apartment.constant_payment,
      constant_part_gcal: apartment.constant_part_gcal,
      variable_payment: apartment.variable_payment,
      variable_part_gcal: apartment.variable_part_gcal,
      delta_payment: apartment.delta_payment,
      total_payment: apartment.total_payment,
      total_gcal: apartment.total_gcal,
      total_area: summary?.total_area,
      data_area: summary?.data_area,
      missing_area: summary?.missing_area,
      tariff: summary?.tariff,
      distribution_scheme: summary
        ? `${summary.variable_share_percent}% / ${summary.constant_share_percent}%`
        : '—',
      reporting_period: summary
        ? `${formatDate(summary.period_start)} - ${formatDate(summary.period_end)}`
        : '—',
      total_house_cost: summary?.total_payments,
      total_house_consumption: summary?.total_house_consumption,
      calculation_basis: apartment.has_data ? 'По показаниям' : 'По площади',
    };

    if (!Array.isArray(apartment.devices) || apartment.devices.length === 0) {
      return [
        {
          ...baseRow,
          room_label: '',
          placement: '',
          coefficient: null,
          reduction_coefficient: null,
          model_name: '',
          serial_number: '',
          previous_value: null,
          current_value: null,
          status: apartment.has_data ? 'OK' : 'Нет показаний',
          raw_delta: null,
          consumption_units: null,
          consumption_gcal: null,
        },
      ];
    }

    return apartment.devices.map((device) => ({
      ...baseRow,
      room_label: device.room_label || '',
      placement: device.placement || '',
      coefficient: device.coefficient,
      reduction_coefficient: device.reduction_coefficient,
      model_name: device.model_name || device.name || '',
      serial_number: device.serial_number || '',
      previous_value: device.previous_value,
      current_value: device.current_value,
      status: device.status || 'OK',
      raw_delta: device.raw_delta,
      consumption_units: device.consumption_units,
      consumption_gcal: device.consumption_gcal,
    }));
  });
}

export default function CalculationsPage() {
  const tenantDataStartDate = useUnit($tenantDataStartDate);
  const [buildings, setBuildings] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [loadingBuildings, setLoadingBuildings] = useState(true);
  const [loading, setLoading] = useState(false);
  const [exportingOverview, setExportingOverview] = useState(false);
  const [error, setError] = useState('');
  const [mode, setMode] = useState('standard');
  const [rows, setRows] = useState([]);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [detailDialog, setDetailDialog] = useState({
    open: false,
    row: null,
    loading: false,
    payload: null,
    exportLoading: false,
  });

  useEffect(() => {
    let active = true;

    const loadBuildings = async () => {
      setLoadingBuildings(true);
      try {
        const items = await buildingsApi.getBuildings(0, 500);
        if (!active) return;
        setBuildings(items || []);
        setSelectedBuilding((current) => current || items?.[0] || null);
      } catch (loadError) {
        console.error('load buildings failed', loadError);
        if (active) {
          setError('Не удалось загрузить список домов.');
          setBuildings([]);
        }
      } finally {
        if (active) {
          setLoadingBuildings(false);
        }
      }
    };

    loadBuildings();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    const loadCalculations = async () => {
      if (!selectedBuilding?.id) {
        setRows([]);
        return;
      }

      setLoading(true);
      setError('');

      try {
        if (selectedBuilding.heat_allocator_enabled) {
          setMode('urt');
          const periods = await heatApi.listPeriods(String(selectedBuilding.id));
          const startDate = dateKey(tenantDataStartDate);
          const periodItems = Array.isArray(periods)
            ? periods
                .filter((period) => {
                  const periodEnd = dateKey(period.period_end);
                  return !startDate || !periodEnd || periodEnd >= startDate;
                })
                .slice(0, 12)
            : [];
          const results = await Promise.allSettled(
            periodItems.map(async (period) => {
              const report = await heatApi.buildReport(String(period.id));
              const byReadings = report.apartments.filter((item) => item.has_data).length;
              const byArea = report.apartments.filter((item) => !item.has_data).length;

              return {
                id: period.id,
                mode: 'urt',
                buildingName: selectedBuilding.name,
                date: period.updated_at || period.created_at,
                reportingPeriod: formatPeriodRange(period.period_start, period.period_end, { dateOnly: true }),
                opuReading: period.house_consumption_gcal,
                distributionScheme: `${period.variable_share_percent ?? report.summary.variable_share_percent}% / ${period.constant_share_percent ?? report.summary.constant_share_percent}%`,
                premises: report.apartments.length,
                byReadings,
                byArea,
                calculationType: deriveUrtCalculationType(report),
                totalCost: report.summary.total_payments,
                tariff: period.tariff ?? report.summary.tariff,
                periodId: period.id,
                periodStart: period.period_start,
                periodEnd: period.period_end,
                summary: report.summary,
              };
            })
          );

          if (!active) return;

          setRows(
            results
              .filter((item) => item.status === 'fulfilled')
              .map((item) => item.value)
          );
        } else {
          setMode('standard');
          const rcId = selectedBuilding.residential_complex_id;
          const startDate = dateKey(tenantDataStartDate);
          const startMonth = monthKey(tenantDataStartDate);
          const months = Array.from({ length: 6 }, (_, index) => localMonthStartDate(index)).filter(
            (periodDate) => !startMonth || monthKey(periodDate) >= startMonth
          );
          const results = await Promise.allSettled(
            months.map(async (periodDate) => {
              const report = await reportsApi.getResidentialConsumptionReport({
                residentialComplexId: rcId,
                buildingId: selectedBuilding.id,
                period: periodDate,
              });

              const devicesTotal = report.rows.length;
              // «С показаниями» = есть текущее показание за период, а не только полная пара ok.
              const withReadings = report.rows.filter((row) => hasPeriodReading(row)).length;
              const completePairs = report.rows.filter((row) => row.status === 'ok').length;
              const withoutReadings = devicesTotal - withReadings;
              const premises = uniqueApartmentCount(report.rows);
              const coverage = devicesTotal > 0 ? (withReadings / devicesTotal) * 100 : 0;

              return {
                id: report.period_end,
                mode: 'standard',
                buildingName: selectedBuilding.name,
                date: report.period_end,
                reportingPeriod: formatPeriodRange(report.previous_period_end, report.period_end),
                premises,
                devicesTotal,
                withReadings,
                withoutReadings,
                coverage,
                calculationType: 'Стандартный',
                basis: deriveStandardBasis(withReadings, withoutReadings, completePairs),
                reportParams: {
                  residentialComplexId: rcId,
                  buildingId: selectedBuilding.id,
                  period: periodDate,
                },
              };
            })
          );

          if (!active) return;

          setRows(
            results
              .filter((item) => item.status === 'fulfilled')
              .map((item) => item.value)
              .filter((row) => !startDate || !dateKey(row.date) || dateKey(row.date) >= startDate)
          );
        }
      } catch (loadError) {
        console.error('load calculations failed', loadError);
        if (active) {
          setRows([]);
          setError('Не удалось загрузить расчёты по выбранному дому.');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadCalculations();
    return () => {
      active = false;
    };
  }, [selectedBuilding, tenantDataStartDate]);

  const latestDate = useMemo(() => {
    if (!rows.length) return null;
    return rows[0]?.date || null;
  }, [rows]);

  const urtFlatDetailRows = useMemo(
    () => (detailDialog.row?.mode === 'urt' ? flattenUrtRows(detailDialog.payload) : []),
    [detailDialog.payload, detailDialog.row?.mode]
  );

  const handleOpenDetail = async (row) => {
    setDetailDialog({
      open: true,
      row,
      loading: true,
      payload: null,
      exportLoading: false,
    });

    try {
      if (row.mode === 'urt') {
        const report = await heatApi.buildReport(String(row.periodId));
        setDetailDialog((current) => ({
          ...current,
          loading: false,
          payload: report,
        }));
      } else {
        const report = await reportsApi.getResidentialConsumptionReport(row.reportParams);
        setDetailDialog((current) => ({
          ...current,
          loading: false,
          payload: report,
        }));
      }
    } catch (detailError) {
      console.error('load calculation detail failed', detailError);
      setDetailDialog((current) => ({
        ...current,
        loading: false,
        payload: null,
      }));
      setError('Не удалось загрузить детальный расчёт.');
    }
  };

  const handleCloseDetail = () => {
    setExportMenuAnchor(null);
    setDetailDialog({
      open: false,
      row: null,
      loading: false,
      payload: null,
      exportLoading: false,
    });
  };

  const handleExportOverviewCsv = () => {
    if (!rows.length) return;
    setExportingOverview(true);
    try {
      const headers =
        mode === 'urt'
          ? [
              'Дата',
              'Отчетный период',
              'Показания ОДПУ',
              'Схема распределения',
              'Помещений',
              'По показаниям',
              'По площади',
              'Тип расчета',
              'Общие затраты дома, ₽',
              'Тариф, ₽',
            ]
          : [
              'Дата',
              'Отчетный период',
              'Помещений',
              'Приборов',
              'С показаниями',
              'Без показаний',
              'Покрытие, %',
              'Тип расчета',
              'Основа начисления',
            ];

      const csvRows =
        mode === 'urt'
          ? rows.map((row) => [
              formatDate(row.date),
              row.reportingPeriod,
              formatNumber(row.opuReading, 3),
              row.distributionScheme,
              row.premises,
              row.byReadings,
              row.byArea,
              row.calculationType,
              formatMoney(row.totalCost),
              formatMoney(row.tariff),
            ])
          : rows.map((row) => [
              formatDate(row.date),
              row.reportingPeriod,
              row.premises,
              row.devicesTotal,
              row.withReadings,
              row.withoutReadings,
              formatNumber(row.coverage, 1),
              row.calculationType,
              row.basis,
            ]);

      const blob = createCsvBlob(headers, csvRows);
      downloadBlobAsFile(
        blob,
        buildFileName(
          ['calculations', selectedBuilding?.name, mode, new Date().toISOString().slice(0, 10)],
          'calculations.csv'
        )
      );
    } finally {
      setExportingOverview(false);
    }
  };

  const handleExportDetail = async (kind) => {
    if (!detailDialog.row) return;
    setDetailDialog((current) => ({ ...current, exportLoading: true }));
    try {
      if (detailDialog.row.mode === 'urt') {
        if (kind === 'receipts-zip') {
          const blob = await heatApi.downloadReceiptsZip(String(detailDialog.row.periodId));
          downloadBlobAsFile(
            blob,
            buildFileName(
              ['billingservice', detailDialog.row.buildingName, detailDialog.row.periodEnd, 'квитанции'],
              'receipts.zip'
            )
          );
        } else if (kind === 'c300') {
          const blob = await heatApi.downloadReportC300(String(detailDialog.row.periodId));
          downloadBlobAsFile(
            blob,
            buildFileName(
              ['billingservice', 'urt', detailDialog.row.buildingName, detailDialog.row.periodEnd, 'С-300'],
              'urt-c300.csv'
            )
          );
        } else if (kind === 'excel-short' || kind === 'excel-full') {
          const excelFormat = kind === 'excel-short' ? 'short' : 'full';
          const blob = await heatApi.downloadReportExcel(
            String(detailDialog.row.periodId),
            excelFormat,
          );
          downloadBlobAsFile(
            blob,
            buildFileName(
              ['urt', detailDialog.row.buildingName, detailDialog.row.periodStart, detailDialog.row.periodEnd, excelFormat],
              'urt-report.xlsx'
            )
          );
        } else {
          const csvRows = urtFlatDetailRows.map((item) => [
            item.apartment_number,
            item.area,
            item.reduction_coefficient,
            item.conditional_consumption,
            item.base_payment,
            item.constant_payment,
            item.constant_part_gcal,
            item.variable_payment,
            item.variable_part_gcal,
            item.delta_payment,
            item.total_payment,
            item.total_gcal,
            item.room_label || item.placement,
            item.coefficient,
            item.model_name,
            item.serial_number,
            item.previous_value,
            item.current_value,
            item.status,
            item.total_area,
            item.data_area,
            item.missing_area,
            item.tariff,
            item.distribution_scheme,
            item.reporting_period,
            item.total_house_cost,
            item.total_house_consumption,
            item.consumption_units,
            item.consumption_gcal,
            item.calculation_basis,
          ]);
          const blob = createCsvBlob(
            [
              'Помещение',
              'Площадь, м²',
              'Пониж. коэффициент',
              'Сумма пересчит. показаний, ед.',
              'Основной расчет, ₽',
              'Постоянная часть, ₽',
              'Постоянная часть, Гкал',
              'Переменная часть, ₽',
              'Переменная часть, Гкал',
              'Перерасчет, ₽',
              'Итого, ₽',
              'Итого, Гкал',
              'Расположение',
              'Коэфф. ОП',
              'Модель распред.',
              '№ распред.',
              'Нач. показ.',
              'Кон. показ.',
              'Состояние',
              'Общая площадь дома, м²',
              'Площадь с данными, м²',
              'Площадь без данных, м²',
              'Тариф, ₽/Гкал',
              'Схема распределения',
              'Отчетный период',
              'Затраты дома, ₽',
              'Потребление дома, Гкал',
              'Количество пересчитанных единиц',
              'Количество Гкал',
              'Расчет: по площади / по показаниям',
            ],
            csvRows
          );
          downloadBlobAsFile(
            blob,
            buildFileName(
              ['urt', detailDialog.row.buildingName, detailDialog.row.periodStart, detailDialog.row.periodEnd],
              'urt-report.csv'
            )
          );
        }
      } else if (kind === 'excel-full' || kind === 'c300') {
        const exportFormat = kind === 'c300' ? 'c300_csv' : 'xlsx';
        const result = await reportsApi.downloadResidentialConsumptionReport(
          detailDialog.row.reportParams,
          exportFormat,
        );
        downloadBlobAsFile(
          result.data,
          result.fileName ||
            buildFileName(
              ['consumption', detailDialog.row.buildingName, detailDialog.row.date, exportFormat],
              exportFormat === 'c300_csv' ? 'consumption-c300.csv' : 'consumption-report.xlsx'
            )
        );
      } else {
        const payload = detailDialog.payload;
        const csvRows = (payload?.rows || []).map((item) => [
          item.apartment_number || '',
          item.corps_name || '',
          item.system_type || '',
          item.device_name || '',
          item.device_serial || '',
          formatNumber(item.current_value, 3),
          formatDateTime(item.current_ts),
          formatNumber(item.previous_value, 3),
          formatDateTime(item.previous_ts),
          formatNumber(item.consumption, 3),
          item.status_label || item.status || '',
        ]);
        const blob = createCsvBlob(
          [
            'Помещение',
            'Корпус',
            'Тип',
            'Прибор',
            'Серийный номер',
            'Текущее значение',
            'Дата текущего',
            'Предыдущее значение',
            'Дата предыдущего',
            'Расход',
            'Статус',
          ],
          csvRows
        );
        downloadBlobAsFile(
          blob,
          buildFileName(
            ['consumption', detailDialog.row.buildingName, detailDialog.row.date],
            'consumption-report.csv'
          )
        );
      }
    } catch (exportError) {
      console.error('export calculation detail failed', exportError);
      setError('Не удалось выгрузить файл расчёта.');
    } finally {
      setDetailDialog((current) => ({ ...current, exportLoading: false }));
    }
  };

  const handleSelectExportFormat = (kind) => {
    setExportMenuAnchor(null);
    handleExportDetail(kind);
  };

  const handleDownloadApartmentReceipt = async (apartmentId, apartmentNumber, accountNumber) => {
    if (!detailDialog.row?.periodId || !apartmentId) return;
    setDetailDialog((current) => ({ ...current, exportLoading: true }));
    try {
      const blob = await heatApi.downloadApartmentReceipt(
        String(detailDialog.row.periodId),
        String(apartmentId),
        'zip'
      );
      downloadBlobAsFile(
        blob,
        buildFileName(
          [
            'billingservice',
            detailDialog.row.buildingName || detailDialog.row.buildingAddress,
            apartmentNumber || 'кв',
            accountNumber || apartmentNumber || apartmentId,
          ],
          'receipt.zip'
        )
      );
    } catch (exportError) {
      console.error('export apartment receipt failed', exportError);
      setError('Не удалось выгрузить квитанцию по квартире.');
    } finally {
      setDetailDialog((current) => ({ ...current, exportLoading: false }));
    }
  };

  const standardDetailRows = detailDialog.payload?.rows || [];

  return (
    <div className="home-container" style={{ marginLeft: 24, marginRight: 24, paddingBottom: 24 }}>
      <Paper
        elevation={0}
        sx={{
          ...surfaceSx,
          p: { xs: 1, md: 1.15 },
          mb: 1,
          borderRadius: 3,
          background: '#fff',
        }}
      >
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={0.8}
          justifyContent="flex-end"
          alignItems={{ xs: 'stretch', lg: 'center' }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={0.75} sx={{ width: '100%' }}>
            <Autocomplete
              size="small"
              loading={loadingBuildings}
              options={buildings}
              value={selectedBuilding}
              onChange={(event, value) => setSelectedBuilding(value)}
              getOptionLabel={(option) => option?.name || ''}
              isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
              slotProps={{ paper: { sx: autocompletePaperSx } }}
              sx={{ flex: { xs: '1 1 auto', md: '0 0 360px' }, ...inputSx }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Дом"
                  placeholder="Выберите дом"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingBuildings ? <CircularProgress color="inherit" size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <Button
              variant="outlined"
              startIcon={<DownloadRoundedIcon />}
              onClick={handleExportOverviewCsv}
              disabled={!rows.length || exportingOverview}
              sx={{ height: 40, borderRadius: 2, px: 2 }}
            >
              CSV
            </Button>
            <Chip
              label={mode === 'urt' ? 'Режим: УРТ' : 'Режим: стандартный'}
              size="small"
              sx={{
                ...countChipSx,
                height: 40,
                borderRadius: 2,
                alignSelf: { xs: 'stretch', md: 'center' },
                justifyContent: 'center',
              }}
            />
          </Stack>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mb: 1.25 }}>
        {[
          summaryChip('Периодов', rows.length),
          summaryChip('Последняя дата', latestDate ? formatDate(latestDate) : '—'),
          summaryChip('Тип дома', mode === 'urt' ? 'С УРТ' : 'Без УРТ'),
        ]}
      </Stack>

      <Paper variant="outlined" sx={{ ...surfaceSx, overflow: 'hidden' }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={0.75}
          sx={{
            px: 1.25,
            py: 0.85,
            borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
            background: 'linear-gradient(180deg, #FFFFFF 0%, #FAFCFF 100%)',
          }}
        >
          <Typography variant="caption" sx={{ color: '#526579', fontWeight: 700 }}>
            {selectedBuilding?.name || 'Дом не выбран'}
          </Typography>
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
            <Chip size="small" label={`Периодов: ${rows.length}`} sx={countChipSx} />
            <Chip
              size="small"
              label={mode === 'urt' ? 'УРТ' : 'Стандартный'}
              sx={countChipSx}
            />
          </Stack>
        </Stack>
        <TableContainer
          sx={{
            maxHeight: 'calc(100vh - 330px)',
            minHeight: 280,
            overflow: 'auto',
            scrollbarGutter: 'stable',
            '&::-webkit-scrollbar': {
              width: 10,
              height: 10,
            },
            '&::-webkit-scrollbar-track': {
              background: '#F5F7FB',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#CBD5E1',
              borderRadius: 999,
              border: '2px solid #F5F7FB',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#94A3B8',
            },
          }}
        >
          <Table stickyHeader size="small" sx={{ minWidth: mode === 'urt' ? 1180 : 980 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...tableHeadCellSx, width: 100 }}>Дата</TableCell>
                <TableCell sx={{ ...tableHeadCellSx, width: 170 }}>Отчетный период</TableCell>
                {mode === 'urt' ? (
                  <>
                    <TableCell sx={tableHeadCellSx}>Показания ОДПУ</TableCell>
                    <TableCell sx={tableHeadCellSx}>Схема</TableCell>
                    <TableCell sx={tableHeadCellSx}>Помещений</TableCell>
                    <TableCell sx={tableHeadCellSx}>По показаниям</TableCell>
                    <TableCell sx={tableHeadCellSx}>По площади</TableCell>
                    <TableCell sx={tableHeadCellSx}>Тип</TableCell>
                    <TableCell sx={tableHeadCellSx}>Затраты, ₽</TableCell>
                    <TableCell sx={tableHeadCellSx}>Тариф, ₽</TableCell>
                  </>
                ) : (
                  <>
                    <TableCell sx={tableHeadCellSx}>Помещений</TableCell>
                    <TableCell sx={tableHeadCellSx}>Приборов</TableCell>
                    <TableCell sx={tableHeadCellSx}>С показаниями</TableCell>
                    <TableCell sx={tableHeadCellSx}>Без показаний</TableCell>
                    <TableCell sx={tableHeadCellSx}>Покрытие, %</TableCell>
                    <TableCell sx={tableHeadCellSx}>Тип</TableCell>
                    <TableCell sx={tableHeadCellSx}>Основа</TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={mode === 'urt' ? 10 : 9} sx={{ py: 6, textAlign: 'center' }}>
                    <CircularProgress size={26} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={mode === 'urt' ? 10 : 9} sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
                    Нет данных для отображения.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id} hover sx={tableRowHoverSx} onClick={() => handleOpenDetail(row)}>
                    <TableCell>{formatDate(row.date)}</TableCell>
                    <TableCell>{row.reportingPeriod}</TableCell>
                    {mode === 'urt' ? (
                      <>
                        <TableCell>{formatNumber(row.opuReading, 3)}</TableCell>
                        <TableCell>{row.distributionScheme}</TableCell>
                        <TableCell>{row.premises}</TableCell>
                        <TableCell>{row.byReadings}</TableCell>
                        <TableCell>{row.byArea}</TableCell>
                        <TableCell>{row.calculationType}</TableCell>
                        <TableCell>{formatMoney(row.totalCost)}</TableCell>
                        <TableCell>{formatMoney(row.tariff)}</TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>{row.premises}</TableCell>
                        <TableCell>{row.devicesTotal}</TableCell>
                        <TableCell>{row.withReadings}</TableCell>
                        <TableCell>{row.withoutReadings}</TableCell>
                        <TableCell>{formatNumber(row.coverage, 1)}</TableCell>
                        <TableCell>{row.calculationType}</TableCell>
                        <TableCell>{row.basis}</TableCell>
                      </>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={detailDialog.open} onClose={handleCloseDetail} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {detailDialog.row?.buildingName || 'Расчёт'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {detailDialog.row?.reportingPeriod || '—'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {detailDialog.row?.mode === 'urt' && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadRoundedIcon />}
                  onClick={() => handleExportDetail('receipts-zip')}
                  disabled={detailDialog.loading || detailDialog.exportLoading || !detailDialog.row}
                >
                  Скачать квитанции
                </Button>
              )}
              <Button
                size="small"
                variant="contained"
                startIcon={<DownloadRoundedIcon />}
                onClick={(event) => setExportMenuAnchor(event.currentTarget)}
                disabled={detailDialog.loading || detailDialog.exportLoading || !detailDialog.row}
                aria-haspopup="menu"
                aria-expanded={Boolean(exportMenuAnchor)}
              >
                Скачать отчёт
              </Button>
              <Menu
                anchorEl={exportMenuAnchor}
                open={Boolean(exportMenuAnchor)}
                onClose={() => setExportMenuAnchor(null)}
              >
                {detailDialog.row?.mode === 'urt' && (
                  <MenuItem onClick={() => handleSelectExportFormat('excel-short')}>
                    Короткий Excel
                  </MenuItem>
                )}
                <MenuItem onClick={() => handleSelectExportFormat('excel-full')}>
                  Полный Excel
                </MenuItem>
                <MenuItem
                  onClick={() => handleSelectExportFormat('csv')}
                  disabled={!detailDialog.payload}
                >
                  Обычный CSV
                </MenuItem>
                <MenuItem onClick={() => handleSelectExportFormat('c300')}>
                  CSV для С-300
                </MenuItem>
              </Menu>
            </Stack>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          {detailDialog.loading ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          ) : detailDialog.row?.mode === 'urt' ? (
            <>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} sx={{ mb: 2 }}>
                {[
                  summaryChip('Схема', detailDialog.row.distributionScheme),
                  summaryChip('Тариф, ₽/Гкал', formatMoney(detailDialog.row.tariff)),
                  summaryChip('Затраты дома, ₽', formatMoney(detailDialog.row.totalCost)),
                  summaryChip('Потребление дома, Гкал', formatNumber(detailDialog.payload?.summary?.total_house_consumption, 3)),
                ]}
              </Stack>
              <Paper variant="outlined" sx={{ ...surfaceSx, overflow: 'hidden' }}>
                <TableContainer
                  sx={{
                    maxHeight: 520,
                    overflow: 'auto',
                    '&::-webkit-scrollbar': { width: 10, height: 10 },
                    '&::-webkit-scrollbar-track': { background: '#F5F7FB' },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#CBD5E1',
                      borderRadius: 999,
                      border: '2px solid #F5F7FB',
                    },
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={tableHeadCellSx}>Помещение</TableCell>
                        <TableCell sx={tableHeadCellSx}>Лицевой счёт</TableCell>
                        <TableCell sx={tableHeadCellSx}>Площадь, м²</TableCell>
                        <TableCell sx={tableHeadCellSx}>Пониж. коэфф.</TableCell>
                        <TableCell sx={tableHeadCellSx}>Сумма пересчит. показаний, ед.</TableCell>
                        <TableCell sx={tableHeadCellSx}>Основной расчет, ₽</TableCell>
                        <TableCell sx={tableHeadCellSx}>Постоянная часть, ₽</TableCell>
                        <TableCell sx={tableHeadCellSx}>Постоянная часть, Гкал</TableCell>
                        <TableCell sx={tableHeadCellSx}>Переменная часть, ₽</TableCell>
                        <TableCell sx={tableHeadCellSx}>Переменная часть, Гкал</TableCell>
                        <TableCell sx={tableHeadCellSx}>Перерасчет, ₽</TableCell>
                        <TableCell sx={tableHeadCellSx}>Итого, ₽</TableCell>
                        <TableCell sx={tableHeadCellSx}>Итого, Гкал</TableCell>
                        <TableCell sx={tableHeadCellSx}>Расположение</TableCell>
                        <TableCell sx={tableHeadCellSx}>Коэфф. ОП</TableCell>
                        <TableCell sx={tableHeadCellSx}>Модель распред.</TableCell>
                        <TableCell sx={tableHeadCellSx}>№ распред.</TableCell>
                        <TableCell sx={tableHeadCellSx}>Нач. показ.</TableCell>
                        <TableCell sx={tableHeadCellSx}>Кон. показ.</TableCell>
                        <TableCell sx={tableHeadCellSx}>Состояние</TableCell>
                        <TableCell sx={tableHeadCellSx}>Расчет</TableCell>
                        <TableCell sx={tableHeadCellSx}>Квитанция</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {urtFlatDetailRows.map((item, index) => (
                        <TableRow key={`${item.apartment_number}-${item.serial_number || 'row'}-${index}`} hover>
                          <TableCell>{item.apartment_number || '—'}</TableCell>
                          <TableCell>{item.account_number || '—'}</TableCell>
                          <TableCell>{formatNumber(item.area, 2)}</TableCell>
                          <TableCell>{formatNumber(item.reduction_coefficient, 3)}</TableCell>
                          <TableCell>{formatNumber(item.conditional_consumption, 4)}</TableCell>
                          <TableCell>{formatMoney(item.base_payment)}</TableCell>
                          <TableCell>{formatMoney(item.constant_payment)}</TableCell>
                          <TableCell>{formatNumber(item.constant_part_gcal, 5)}</TableCell>
                          <TableCell>{formatMoney(item.variable_payment)}</TableCell>
                          <TableCell>{formatNumber(item.variable_part_gcal, 5)}</TableCell>
                          <TableCell>{formatMoney(item.delta_payment)}</TableCell>
                          <TableCell>{formatMoney(item.total_payment)}</TableCell>
                          <TableCell>{formatNumber(item.total_gcal, 5)}</TableCell>
                          <TableCell>{item.room_label || item.placement || '—'}</TableCell>
                          <TableCell>{formatNumber(item.coefficient, 3)}</TableCell>
                          <TableCell>{item.model_name || '—'}</TableCell>
                          <TableCell>{item.serial_number || '—'}</TableCell>
                          <TableCell>{formatNumber(item.previous_value, 3)}</TableCell>
                          <TableCell>{formatNumber(item.current_value, 3)}</TableCell>
                          <TableCell>{item.status || '—'}</TableCell>
                          <TableCell>{item.calculation_basis}</TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              startIcon={<DownloadRoundedIcon />}
                              disabled={detailDialog.exportLoading || !item.apartment_id}
                              onClick={() =>
                                handleDownloadApartmentReceipt(
                                  item.apartment_id,
                                  item.apartment_number,
                                  item.account_number
                                )
                              }
                            >
                              Excel+PDF
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </>
          ) : (
            <>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} sx={{ mb: 2 }}>
                {[
                  summaryChip('Приборов', standardDetailRows.length),
                  summaryChip('Помещений', uniqueApartmentCount(standardDetailRows)),
                  summaryChip(
                    'С показаниями',
                    standardDetailRows.filter((item) => hasPeriodReading(item)).length
                  ),
                  summaryChip(
                    'Без показаний',
                    standardDetailRows.filter((item) => !hasPeriodReading(item)).length
                  ),
                ]}
              </Stack>
              <Paper variant="outlined" sx={{ ...surfaceSx, overflow: 'hidden' }}>
                <TableContainer
                  sx={{
                    maxHeight: 520,
                    overflow: 'auto',
                    '&::-webkit-scrollbar': { width: 10, height: 10 },
                    '&::-webkit-scrollbar-track': { background: '#F5F7FB' },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#CBD5E1',
                      borderRadius: 999,
                      border: '2px solid #F5F7FB',
                    },
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={tableHeadCellSx}>Помещение</TableCell>
                        <TableCell sx={tableHeadCellSx}>Корпус</TableCell>
                        <TableCell sx={tableHeadCellSx}>Тип</TableCell>
                        <TableCell sx={tableHeadCellSx}>Прибор</TableCell>
                        <TableCell sx={tableHeadCellSx}>Серийный номер</TableCell>
                        <TableCell sx={tableHeadCellSx}>Текущее</TableCell>
                        <TableCell sx={tableHeadCellSx}>Предыдущее</TableCell>
                        <TableCell sx={tableHeadCellSx}>Расход</TableCell>
                        <TableCell sx={tableHeadCellSx}>Статус</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {standardDetailRows.map((item) => (
                        <TableRow key={item.device_id} hover>
                          <TableCell>{item.apartment_number || '—'}</TableCell>
                          <TableCell>{item.corps_name || '—'}</TableCell>
                          <TableCell>{item.system_type || '—'}</TableCell>
                          <TableCell>{item.device_name || '—'}</TableCell>
                          <TableCell>{item.device_serial || '—'}</TableCell>
                          <TableCell>{formatNumber(item.current_value, 3)}</TableCell>
                          <TableCell>{formatNumber(item.previous_value, 3)}</TableCell>
                          <TableCell>{formatNumber(item.consumption, 3)}</TableCell>
                          <TableCell>{item.status_label || item.status || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetail}>Закрыть</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
